import { Radio, TableProps, Tooltip } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';

import ResizableTable from '../../../foundation/components/resizable_table/ResizableTable';
import { useAppDispatch } from '../../../store/hooks';
import {
  useGetFrequencyRatiosQuery,
  useGetGeneTopPatVarFrequenciesQuery,
  useGetVariantDetailQuery,
  useUpdateGeneTopVariantsThresholdMutation,
} from '../../api/variantApi';
import LoadMoreBtn from '../components/LoadMoreBtn';
import { VARIANT_STAGE_TYPES } from '../constants';
import { useVariantStep } from '../hooks/use_variant_step';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { setFrequencyDataNeedsRecalculation } from '../redux';
import { GeneTopPatVarFrequencies } from '../redux';

const LOAD_MORE_CUTTOFF_LENGTH = 1;
const FrequentPathogenicVariantSection = () => {
  const dispatch = useAppDispatch();
  const params = useParams();
  const [showMore, setShowMore] = useState(false);

  // Read the selected variant straight from the RTK Query cache (populated
  // by the workflow loader) rather than a Redux selector.
  const { data: variant } = useGetVariantDetailQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );
  const { data: ratios } = useGetFrequencyRatiosQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );
  const { canEditWorkflow } = useVariantAccess();
  const stageInfo = useVariantStep(VARIANT_STAGE_TYPES.FREQUENCY_DATA);
  const [updateThreshold] = useUpdateGeneTopVariantsThresholdMutation();
  const isThresholdEditDisabled =
    !canEditWorkflow ||
    (stageInfo?.status !== 'revised' && stageInfo?.status !== 'not_started');
  // Read pathogenic-variant frequencies straight from the query hook's cache
  const { data: geneTopPatVarFrequencies } =
    useGetGeneTopPatVarFrequenciesQuery(
      { gene_id: variant?.gene_id },
      { skip: !variant?.gene_id },
    );
  const dataSource: GeneTopPatVarFrequencies[] = geneTopPatVarFrequencies ?? [];

  const thresholdPathogenicVariant = useMemo(
    () =>
      dataSource.find((item) => item.selected_to_override_max_af) ??
      dataSource[0] ??
      null,
    [dataSource],
  );

  const handleSetThreshold = useCallback(
    (record: GeneTopPatVarFrequencies) => {
      if (!(isThresholdEditDisabled || !variant?.gene_id)) {
        updateThreshold({
          gene_id: variant.gene_id,
          allele_frequency: record.allelefrequency,
          overriden_max_af_clinvar_id: record.clinvar_id,
          variant_id: variant.id,
        });
        dispatch(setFrequencyDataNeedsRecalculation(true));
      } else {
        return;
      }
    },
    [
      isThresholdEditDisabled,
      variant?.gene_id,
      variant?.id,
      updateThreshold,
      dispatch,
    ],
  );

  const renderThreshold = useCallback(
    (_value: boolean, record: GeneTopPatVarFrequencies) => {
      const isThreshold =
        thresholdPathogenicVariant?.variant_description_column ===
        record.variant_description_column;
      return (
        <div className="h-table-action-col">
          <span style={{ display: 'inline-block' }}>
            <Radio
              checked={isThreshold}
              disabled={isThreshold || isThresholdEditDisabled}
              onChange={() => handleSetThreshold(record)}
            />
          </span>
        </div>
      );
    },
    [thresholdPathogenicVariant, isThresholdEditDisabled, handleSetThreshold],
  );

  const columns: TableProps<GeneTopPatVarFrequencies>['columns'] = useMemo(
    () => [
      {
        title: 'Variant',
        dataIndex: 'variant_description_column',
        key: 'variant_description_column',
        render: (text) => (
          <span>
            <strong className="h-bold-column">{text}</strong>
          </span>
        ),
      },
      {
        title: 'Source',
        dataIndex: 'source_cleaned',
        key: 'source_cleaned',
      },
      {
        title: 'HGVS consequence',
        dataIndex: 'hgvs_consequence',
        key: 'hgvs_consequence',
      },
      {
        title: 'VEP annotation',
        dataIndex: 'vep_annotation_cleaned',
        key: 'vep_annotation_cleaned',
      },
      {
        title: 'Clinical significance',
        dataIndex: 'clinical_significance',
        key: 'clinical_significance',
      },
      {
        title: 'Allele count',
        dataIndex: 'allelecount',
        key: 'allelecount',
      },
      {
        title: 'Allele frequency [%]',
        dataIndex: 'allelefrequency_percentage',
        key: 'allelefrequency_percentage',
      },
      {
        title: 'Set as threshold',
        dataIndex: 'selected_to_override_max_af',
        key: 'selected_to_override_max_af',
        render: renderThreshold,
      },
    ],
    [renderThreshold],
  );

  const handleShowMoreClick = () => {
    setShowMore((v) => !v);
  };

  const tableDataSource = useMemo(() => {
    if (showMore) {
      // when extended show all most freq known pathogenic variants sorted in descending order of frequency
      return [...dataSource].sort((a, b) => {
        // convert allele frequencies to numbers for comparison
        const freqA = parseFloat(a.allelefrequency);
        const freqB = parseFloat(b.allelefrequency);
        return freqB - freqA;
      });
    } else {
      // when not extended, show only the threshold variant (or top variant as fallback)
      return thresholdPathogenicVariant
        ? [thresholdPathogenicVariant]
        : dataSource.slice(0, LOAD_MORE_CUTTOFF_LENGTH);
    }
  }, [showMore, dataSource, thresholdPathogenicVariant]);

  return (
    <div className="frequency-data__frequent-pathogenic-section">
      <div className="frequency-data__frequent-pathogenic-header">
        Most frequent known pathogenic variant in gene
        {ratios?.vaf_paf_ratio && (
          <Tooltip
            title={ratios.vaf_paf_ratio.tooltip}
            overlayClassName="molecular-impact__tooltip"
            placement="bottom"
          >
            <span className="frequency-data__ratio-badge">
              {ratios.vaf_paf_ratio.label}
            </span>
          </Tooltip>
        )}
      </div>
      <ResizableTable
        pagination={false}
        columns={columns}
        dataSource={tableDataSource}
        rowKey={(record) => record.variant_description_column}
        persistenceKey="frequent-pathogenic-variants"
      />
      {dataSource.length > LOAD_MORE_CUTTOFF_LENGTH && (
        <LoadMoreBtn
          showMore={showMore}
          handleShowMoreClick={handleShowMoreClick}
        />
      )}
    </div>
  );
};

export default FrequentPathogenicVariantSection;
